import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, TrendingUp, User, Star, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { memo, useMemo } from "react";
import { useOptimizedImage } from "@/hooks/useOptimizedImage";

// Memoized hero component for performance
export const HeroWithImagery = memo(() => {
  const navigate = useNavigate();

  // Preload critical hero image with optimized URLs
  const heroImage = useOptimizedImage(
    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&h=1080&fit=crop",
    { width: 1920, quality: 85 }
  );

  // Featured trainer images - memoized to prevent re-renders
  const featuredImages = useMemo(() => [
    "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=100&h=100&fit=crop",
    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=100&h=100&fit=crop",
    "https://images.unsplash.com/photo-1581009146145-b5ef050c149a?w=100&h=100&fit=crop",
  ], []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0">
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img 
          src={heroImage.src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          // @ts-ignore - fetchpriority is valid HTML but React types don't include it yet
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Animated shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            {/* Badge */}
            <Badge 
              variant="secondary" 
              className="mb-6 px-4 py-2 text-sm font-medium bg-primary/10 text-primary border-primary/20 backdrop-blur-sm"
            >
              <User className="w-4 h-4 mr-2" />
              #1 Plataforma de Entrenadores en España
            </Badge>

            {/* Main headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6" style={{ fontSynthesis: 'none' }}>
              Tu Entrenador Personal
              <span className="block mt-2 gradient-text" style={{ minHeight: '1.2em', display: 'block' }}>
                Verificado y Cerca de Ti
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-8 leading-relaxed">
              Conecta con entrenadores personales certificados de tu ciudad. 
              Reserva online, paga de forma segura y transforma tu vida con profesionales validados por tu comunidad.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 bg-gradient-primary hover:opacity-90 transition-all shadow-lg shadow-primary/25"
                onClick={() => navigate("/trainers")}
              >
                <User className="w-5 h-5 mr-2" />
                Buscar Entrenador
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 border-2 backdrop-blur-sm bg-background/50"
                onClick={() => navigate("/become-trainer")}
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                Soy Entrenador
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 md:gap-10">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-foreground">+800</p>
                  <p className="text-sm text-muted-foreground">Entrenadores</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <Star className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-foreground">4.9/5</p>
                  <p className="text-sm text-muted-foreground">Valoración</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-foreground">157</p>
                  <p className="text-sm text-muted-foreground">Ciudades</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Image Grid */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Main featured image - optimized loading */}
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&h=700&fit=crop"
                  alt="Entrenador personal trabajando con cliente"
                  className="w-full h-[500px] object-cover"
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">María García</p>
                      <p className="text-white/80 text-sm">⭐ 4.9 • Madrid</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating cards - lazy load decorative images */}
              <div className="absolute -top-4 -left-4 z-20 p-4 rounded-xl bg-background shadow-xl border animate-bounce-slow">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                    <img 
                      src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium">+25k clientes</p>
                    <p className="text-xs text-muted-foreground">satisfechos</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 z-20 p-4 rounded-xl bg-background shadow-xl border">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {featuredImages.map((img, i) => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-background overflow-hidden bg-muted">
                        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm font-medium">+500 hoy</p>
                </div>
              </div>

              {/* Decorative elements - CSS only, no images */}
              <div className="absolute -z-10 top-10 -right-10 w-40 h-40 rounded-full bg-primary/30 blur-2xl" />
              <div className="absolute -z-10 -bottom-10 -left-10 w-60 h-60 rounded-full bg-accent/30 blur-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
});
